/**
 * XP y niveles. Curva CONFIGURABLE: cuadrática por defecto (cada nivel cuesta
 * más), o umbrales explícitos. Puro y determinista.
 */

export interface XpCurve {
  /** XP necesaria para alcanzar el nivel `n` (n≥1, nivel 1 = 0 XP). */
  xpForLevel(level: number): number;
}

/** Curva cuadrática: xpForLevel(n) = base * (n-1)^2. */
export function quadraticCurve(base = 100): XpCurve {
  return {
    xpForLevel(level: number): number {
      const l = Math.max(1, Math.floor(level));
      return base * (l - 1) * (l - 1);
    },
  };
}

/** Curva por umbrales explícitos (thresholds[0] = nivel 1 = 0). */
export function thresholdCurve(thresholds: readonly number[]): XpCurve {
  return {
    xpForLevel(level: number): number {
      const l = Math.max(1, Math.floor(level));
      if (l - 1 < thresholds.length) {
        return thresholds[l - 1] ?? 0;
      }
      // Extrapola con el último salto conocido.
      const last = thresholds[thresholds.length - 1] ?? 0;
      const prev = thresholds[thresholds.length - 2] ?? 0;
      const step = last - prev;
      return last + step * (l - thresholds.length);
    },
  };
}

export const DEFAULT_CURVE: XpCurve = quadraticCurve(100);

/** Nivel alcanzado con `xp` puntos (nivel 1 = 0 XP). */
export function levelForXp(xp: number, curve: XpCurve = DEFAULT_CURVE): number {
  const x = Math.max(0, xp);
  let level = 1;
  // Sube mientras el umbral del siguiente nivel se cumpla. Acotado por seguridad.
  while (level < 10_000 && curve.xpForLevel(level + 1) <= x) {
    level += 1;
  }
  return level;
}

export interface LevelProgress {
  level: number;
  /** XP dentro del nivel actual. */
  intoLevel: number;
  /** XP total que exige el nivel actual→siguiente. */
  levelSpan: number;
  /** 0..1 hacia el siguiente nivel. */
  ratio: number;
  xpToNext: number;
}

export function levelProgress(xp: number, curve: XpCurve = DEFAULT_CURVE): LevelProgress {
  const level = levelForXp(xp, curve);
  const floor = curve.xpForLevel(level);
  const ceil = curve.xpForLevel(level + 1);
  const span = Math.max(1, ceil - floor);
  const into = Math.max(0, xp - floor);
  return {
    level,
    intoLevel: into,
    levelSpan: span,
    ratio: Math.max(0, Math.min(1, into / span)),
    xpToNext: Math.max(0, ceil - xp),
  };
}
